import {
  createFileRoute,
  Navigate,
  Outlet,
  redirect,
  isRedirect,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { refreshManager } from "@/lib/auth";
import { useCompanyStore } from "@/store";
import { useAuthGuard } from "@/hooks/use-auth-guard";
import { useMyCompanyContext, resolveActiveMembership } from "@/hooks/use-company-context";
import { usePersona } from "@/hooks/use-persona";
import { isCompanyAdminRole } from "@/hooks/use-company-role";
import { hasLiveTesterArm } from "@/lib/resolve-persona";
import { CompanySidebar } from "@/components/company-sidebar";
import { CompanySetupShell } from "@/components/company-setup-shell";
import { RequestPending } from "@/components/request-pending";
import { GetStarted } from "@/components/get-started";
import { TesterInviteAcceptance } from "@/components/tester-invite-acceptance";
import { SessionOffline } from "@/components/session-offline";
import { RequiresConnection } from "@/components/requires-connection";
import { InstallPrompt } from "@/components/install-prompt";
import { UnsyncedWorkBanner } from "@/components/unsynced-work-banner";
import { useIsOnlineSettled } from "@/hooks/use-is-online-settled";
import { resolveOfflineRoute } from "@/lib/offline-route-policy";
import { resolveTesterChrome } from "@/lib/tester-chrome-policy";
import { FieldworkStorageProvider } from "@/lib/fieldwork/storage-provider";
import { FieldworkSyncProvider } from "@/lib/fieldwork/sync-provider";
import { PageSkeleton } from "@/components/ui";

export const Route = createFileRoute("/_company")({
  beforeLoad: async ({ context, location }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({
        to: "/login",
        search: { redirect: location.href },
      });
    }
    // Proactive refresh: ensure a valid access token exists before rendering.
    const state = useCompanyStore.getState();
    if (!state.expiresAt || Date.now() >= state.expiresAt - 10_000) {
      try {
        const result = await refreshManager.refreshOutcome();
        if (result.outcome === "rejected") {
          throw redirect({
            to: "/login",
            search: { redirect: location.href },
          });
        }
        // `offline` proceeds into the shell with the pinned identity -- an
        // offline navigation must not evict a tester mid-shift.
      } catch (e) {
        if (isRedirect(e)) throw e;
        // `refreshOutcome()` resolves on every classified arm, so anything
        // thrown here is genuinely unexpected -- fail closed to login.
        throw redirect({
          to: "/login",
          search: { redirect: location.href },
        });
      }
    }
  },
  component: CompanyLayout,
});

// Where MFA enrolment lives. The layout knows the path for one reason: the
// `mfaRequired` termination rule below has to recognise the screen it is
// sending the caller to.
const SETTINGS_PATH = "/settings";

function CompanyLayout() {
  useAuthGuard();
  const { resolution } = usePersona();
  const contextQuery = useMyCompanyContext();
  const activeCompanyId = useCompanyStore((s) => s.activeCompanyId);
  const setActiveCompanyId = useCompanyStore((s) => s.setActiveCompanyId);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // The SETTLED signal, never the raw one: the gate below unmounts the routed
  // surface, and `navigator.onLine` flips on sleep/wake and VPN toggles.
  const isOnline = useIsOnlineSettled();

  // Member-persona state only: `active` is null for a session with no
  // membership, so it gates the sidebar's company entries -- never portal
  // admission. At most one live membership exists, so the list is one row or
  // none and the resolver just reads it.
  const active = resolveActiveMembership(contextQuery.data?.companies ?? [], activeCompanyId);

  // The setup gate's standing — the SAME settled row `/` resumes the wizard
  // from: an admitted session whose membership carries the ADMIN role and a
  // non-empty completeness.missing set has one action, finishing setup, so the
  // admitted arm below swaps the portal shell for the bare one. The carve-outs
  // are in the read itself: a membership row that has not settled (probe in
  // flight, or an offline mount with no cached row) leaves `active` null, so an
  // unresolved session keeps the normal shell rather than flickering the gate;
  // and the create-or-join standing — a live grant with NO membership — keeps
  // the full portal, because an approved independent tester has no company to
  // finish and the wizard is not their unfinished state.
  const companySetupIncomplete =
    resolution.kind === "admitted" &&
    active !== null &&
    isCompanyAdminRole(active.role) &&
    active.completeness.missing.length > 0;

  // Self-heal a stale persisted selection (membership ended, company merged).
  useEffect(() => {
    if (active && active.company_id !== activeCompanyId) {
      setActiveCompanyId(active.company_id);
    }
  }, [active, activeCompanyId, setActiveCompanyId]);

  // The offline CTA points at the tester arm, so it is offered only to a
  // persona whose tester grant is LIVE: `mfaRequired`'s tester arm is enrolment
  // itself, which is server-only, and a revoked grant has no queue to walk to.
  const offlineTesterCta = hasLiveTesterArm(resolution);

  // One shell shape for every admitted persona WHOSE COMPANY SETUP IS COMPLETE
  // (the incomplete-admin standing gets the bare `CompanySetupShell` instead):
  // the tree stays structurally identical across resolution changes, so a
  // persona re-resolution while the enrolment screen holds single-issue
  // recovery codes never remounts the outlet under it. The setup gate's own
  // shape swap happens only on a completeness flip, and the wizard's final
  // write is what earns it.
  //
  // Only the OUTLET is connectivity-gated -- the nav stays, so a stranded dual
  // persona can walk to the arm that still works. The gate runs strictly INSIDE
  // an admitted shell; `unresolvedOffline` and every waiting screen are decided
  // by the persona switch below and never reach it.
  //
  // Swapping the outlet for the panel unmounts the routed surface and discards
  // whatever is half-typed on it, which is why the connectivity signal here is
  // the SETTLED one: an outage has to hold for `OFFLINE_SETTLE_MS` before the
  // swap, while the recovery edge is immediate.
  //
  // The fieldwork provider wraps the WHOLE shell, nav included, because the
  // nav's unsynced badge reads the queue from it -- and `useFieldworkStorage`
  // returns a not-ready default rather than throwing, so a sidebar mounted
  // outside it would quietly report an empty queue to a dual-persona tester
  // standing in the company workspace. Scoping is the provider's own job: it
  // resolves nothing without a tester arm, so a company-only session still
  // opens no database from here.
  //
  // The sync provider inside it is the ONE mount of the sync triggers
  // (launch, focus/visibility, connectivity-regain, the manual button): a
  // utility-side rejection or status change then reaches every screen the
  // tester can be standing on, not only My Work where the SyncBar renders.
  // Mounting it here rather than on any one route is what keeps exactly one
  // registration app-wide.
  const shell = (options: { testerArm: boolean; mfaRequired?: boolean }) => (
    <FieldworkStorageProvider>
      <FieldworkSyncProvider>
        {/* The frame is as tall as the VISIBLE viewport (`h-dvh`): a phone's
            `100vh` is its height with the browser toolbars retracted, so a
            `h-screen` frame hides its own bottom edge under them while they
            show. The print variants undo this dynamic-height scroll frame: with
            an `overflow-auto` child it prints exactly one viewport and drops
            every sheet after the first. Below `md` the frame is a column —
            the sidebar's static top bar, then `<main>` — and from `md` a row. */}
        <div className="flex h-dvh flex-col overflow-hidden bg-surface md:flex-row print:h-auto print:overflow-visible">
          <CompanySidebar
            active={active}
            hasTesterArm={options.testerArm}
            mfaRequired={options.mfaRequired}
          />
          <main className="flex-1 overflow-auto print:h-auto print:overflow-visible">
            {resolveOfflineRoute(pathname, isOnline) === "requiresConnection" ? (
              <RequiresConnection showTesterCta={offlineTesterCta} />
            ) : (
              <>
                {/* The durability chrome sits here rather than on a route,
                    because both pieces have to outlive any one of them. The
                    shell is the capture routes' ANCESTOR, so this mount point is
                    not by itself what keeps a standing install prompt off the
                    screen where a tester is filling out a test —
                    `resolveTesterChrome` is. The prompt is an offer, not a
                    switch: a browser tab captures, queues and syncs exactly as
                    an installed app does.

                    The persona conjunct is the other half, and it is
                    load-bearing now that `/` is shared: a member-only session
                    must not be shown an unsynced-work banner about a queue it
                    cannot have. */}
                {resolveTesterChrome(pathname) === "chrome" && options.testerArm && (
                  <div className="space-y-4 p-4 pb-0 md:px-8 md:pt-8">
                    <InstallPrompt isTesterSurface />
                    <UnsyncedWorkBanner />
                  </div>
                )}
                <Outlet />
              </>
            )}
          </main>
        </div>
      </FieldworkSyncProvider>
    </FieldworkStorageProvider>
  );

  switch (resolution.kind) {
    case "loading":
      return (
        <div className="min-h-dvh bg-surface p-4 sm:p-8">
          <PageSkeleton />
        </div>
      );
    case "admitted":
      // Setup outstanding: the session is admitted but confined — the bare
      // shell owns the whole screen (no sidebar to walk away with) and sends
      // every non-`/` route to `/`, where the wizard and the suspension
      // banner above it fill the page. A live tester arm does not buy the
      // dual persona out of it: the company must be whole first.
      if (companySetupIncomplete) return <CompanySetupShell />;
      // The work entries ride a LIVE grant; a membership-only session (or one
      // whose only grant is revoked) gets Home, Utilities and the company
      // entries. Both read the one nav array.
      return shell({ testerArm: hasLiveTesterArm(resolution) });
    case "mfaRequired":
      // Termination first: the resolution stays `mfaRequired` for the whole
      // visit to the enrolment screen, so a redirect issued THERE re-fires on
      // arrival forever. Rendering on the matched path is what ends the loop.
      // A session with no membership has nothing else to be on, so it is sent
      // to the screen; a dual persona renders wherever it stands, behind a nav
      // collapsed to that one entry.
      if (pathname !== SETTINGS_PATH && !resolution.member) {
        return <Navigate to={SETTINGS_PATH} replace />;
      }
      return shell({ testerArm: false, mfaRequired: true });
    case "invitePending":
      return <TesterInviteAcceptance />;
    case "requestPending":
      return <RequestPending grants={resolution.grants} />;
    case "getStarted":
      return <GetStarted revokedGrants={resolution.revokedGrants} />;
    case "unresolvedOffline":
      // A connectivity state, never a resting one -- a waiting screen here
      // would tell an offline tester their grant has not arrived yet.
      return <SessionOffline />;
  }
}
